import { Currency } from '@ps/web-core-enums';
import type { Website } from '@ps/web-martech-cms-types';

import type {
  Currency as CurrencyName,
  Sites as SitesName
} from '@swf/generated/graphql/poker/CG/schemas';
import type { NavigationTab } from '@swf/poker';
import { GameView } from '@swf/poker';

import { TXTCLI_HoldXem, TXTCLI_Omaha } from './constants.messages.json';
import styles from './styles/shared.scss';
import type { AvailableNavigationTabs, NavigationTabTypes, NavigationTabUrlKey } from './types';
import { NavigationTabs } from './types';

export const GRAPHQL_API_PATHNAME = '/poker/cashgames/v1/graphql';

const NAVIGATION_TABS_TYPE_URL_MAP = {
  [NavigationTabs.HOLDEM]: 'holdem',
  [NavigationTabs.OMAHA]: 'omaha'
} as const satisfies {
  [key in AvailableNavigationTabs]: NavigationTabUrlKey;
};

export const NAVIGATION_DATA = {
  HOLDEM: {
    title: TXTCLI_HoldXem,
    shortTitle: TXTCLI_HoldXem,
    tabName: NavigationTabs.HOLDEM,
    tabActiveStyle: styles.tabActiveHoldem,
    alias: 'pokerCashGames',
    routeValues: {
      tab: NAVIGATION_TABS_TYPE_URL_MAP[NavigationTabs.HOLDEM],
      view: GameView.Simple
    }
  },
  OMAHA: {
    title: TXTCLI_Omaha,
    shortTitle: TXTCLI_Omaha,
    tabName: NavigationTabs.OMAHA,
    tabActiveStyle: styles.tabActiveOmaha,
    alias: 'pokerCashGames',
    routeValues: {
      tab: NAVIGATION_TABS_TYPE_URL_MAP[NavigationTabs.OMAHA],
      view: GameView.Simple
    }
  }
} as const satisfies Record<AvailableNavigationTabs, NavigationTab<NavigationTabTypes>>;

export const NAVIGATION_TABS = [
  NAVIGATION_DATA.HOLDEM,
  NAVIGATION_DATA.OMAHA
] as const satisfies readonly NavigationTab<NavigationTabTypes>[];

export const CURRENCY_NAME_CURRENCY_MAP = {
  CURRENCY_CAD: Currency.CanadianDollar,
  CURRENCY_EUR: Currency.Euro,
  CURRENCY_GBP: Currency.BritishPound,
  CURRENCY_PLAY_MONEY: Currency.PlayMoney,
  CURRENCY_USD: Currency.USDollar,
  CURRENCY_UNSPECIFIED: Currency.USDollar
} as const satisfies { [K in CurrencyName]: Currency };

export const SITE_NAME_SITE_VARIABLE_MAP = {
  ARBA: 'SITES_ARBA',
  ASIA: 'SITES_ASIA',
  BEL: 'SITES_BEL',
  BUL: 'SITES_BUL',
  CA_ON: 'SITES_CA_ON',
  CH: 'SITES_CH',
  COM: 'SITES_COM',
  CZE: 'SITES_CZE',
  DE: 'SITES_DE',
  DE_SH: 'SITES_DE_SH',
  DEN: 'SITES_DEN',
  EST: 'SITES_EST',
  SPA: 'SITES_SPA',
  EU: 'SITES_EU',
  FRA: 'SITES_FRA',
  GR: 'SITES_GR',
  IND: 'SITES_IND',
  ITA: 'SITES_ITA',
  IT_SISAL_EXT: 'SITES_IT_SISAL_EXT',
  IT_PS_EXT: 'SITES_IT_PS_EXT',
  UK: 'SITES_UK',
  US_CO: 'SITES_US_CO',
  USMI: 'SITES_USMI',
  US_NJ: 'SITES_US_NJ',
  US_PA: 'SITES_US_PA',
  BE: 'SITES_BEL',
  BG: 'SITES_BUL',
  CAON: 'SITES_CA_ON',
  CZ: 'SITES_CZE',
  DESH: 'SITES_DE_SH',
  DK: 'SITES_DEN',
  EE: 'SITES_EST',
  ES: 'SITES_SPA',
  FR: 'SITES_FRA',
  IN: 'SITES_IND',
  IT: 'SITES_ITA',
  RO: 'SITES_ROM',
  SE: 'SITES_SWE',
  PT: 'SITES_POR',
  SISAL: 'SITES_IT_SISAL_EXT',
  USCO: 'SITES_US_CO',
  USNJ: 'SITES_US_NJ',
  USPA: 'SITES_US_PA',
  NET: 'SITES_NET',
  NL: 'SITES_NL',
  POR: 'SITES_POR',
  ROM: 'SITES_ROM',
  RU: 'SITES_RU',
  RUSO: 'SITES_RUSO',
  RUSOPM: 'SITES_RUSOPM',
  SWE: 'SITES_SWE'
} as const satisfies { [K in Website['site']]: SitesName };

export const WEB_POKER_GAME_TYPE = {
  POKER_GAME_TYPE_HOLDEM: 2,
  POKER_GAME_TYPE_OMAHA: 4
} as const satisfies {
  [K in 'POKER_GAME_TYPE_OMAHA' | 'POKER_GAME_TYPE_HOLDEM']: number;
};

export const WEB_POKER_GAME_STRUCTURE = {
  POKER_BET_LIMIT_UNSPECIFIED: 0,
  POKER_BET_LIMIT_LIMIT: 1,
  POKER_BET_LIMIT_POT_LIMIT: 2,
  POKER_BET_LIMIT_NO_LIMIT: 3,
  POKER_BET_LIMIT_POT_NO_LIMIT: 4,
  POKER_BET_LIMIT_MULTI_GAME_MIXED: 100
};

export const WEB_POKER_TABLE_TYPE = {
  POKER_TABLE_TYPE_FAST_FOLD: 'zoomparam',
  POKER_TABLE_TYPE_REGULAR: 'ringparam',
  POKER_TABLE_TYPE_UNSPECIFIED: 'test'
};

export const WEB_POKER_CURRENCY_VALUES = {
  CURRENCY_CAD: 6,
  CURRENCY_EUR: 5,
  CURRENCY_GBP: 7,
  CURRENCY_PLAY_MONEY: 1,
  CURRENCY_USD: 4
} as const satisfies { [K in Exclude<CurrencyName, 'CURRENCY_UNSPECIFIED'>]: number };
